import React from 'react';
import { SeatingTabClass, SeatingTabSeat } from 'Services/Api/_HumanWritten/SeatingService';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { DeckFeature, markerLegend } from 'Modules/Ferries/Utils/deckFeatures';
import { seatState } from './seatingLayout';
import SeatingDeckGrid, { SeatAppearance } from './SeatingDeckGrid';

/**
 * How each state is drawn.
 *
 * <p><b>Colour answers one question: can I sit here.</b> Green is yes. Blue is the seat already held.
 * Grey is gone. A class's own colour is spent on the one case where knowing the class helps — a seat
 * this booker cannot take <i>because</i> it belongs to another class — so the colours read as "not
 * yours" rather than as decoration.</p>
 *
 * <p>It used to paint every classed seat in its class colour, available or not. That left the
 * "Available" key pointing at a green nothing on the plan was drawn in, and the booker had to work
 * out which of eight colours meant they could sit there.</p>
 */
const SEAT_STATE_COLOURS = {
	// Blue is reserved for the seat the booker holds, so nothing else competes with it.
	SELECTED: '#1d4ed8',
	TAKEN_BY_THIS_BOOKING: '#a5b4fc',
	TAKEN: '#e5e7eb',
	// Replaced per seat by the class's own colour — the only place a class colour is now used.
	NOT_IN_YOUR_CLASS: '#e5e7eb',
	// Not a seat at all — a wheelchair bay. Lighter than "taken", because it is not competing for
	// the booker's attention: there is nothing to want.
	NOT_A_SEAT: '#f3f4f6',
	// Key only. Nothing on the plan is drawn in it — it stands for choosing nothing, which is what
	// the booker gets by leaving the plan alone.
	NOT_CHOSEN: '#e2e8f0',
	// Green for free, which needs no explaining, and used for every seat this booker can take whether
	// or not it carries a fare type.
	//
	// Deeper than the mint it started as: the labels are white, and white on that mint measured about
	// 1.8:1 — well under the 4.5:1 a seat number has to clear to be read at 10px. Deliberately not the
	// #2e7d32 the design draws, because that green is already the availability rail on the trip cards
	// and one colour cannot mean two things inside one booking.
	AVAILABLE: '#16a34a',
	// A seat in another fare type, offered to a manager seating an overbooked passenger. Amber, not
	// green: it can be taken, but it is not this passenger's own, and the plan should say so.
	OTHER_FARE_TYPE: '#d97706',
} as const;

/** The space between one side of the vessel and the next. Matches the admin plan's own gap. */
const SIDE_GAP = 24;

type SeatStateKey = keyof typeof SEAT_STATE_COLOURS;

interface Props {
	seats: SeatingTabSeat[];
	passengerClasses: SeatingTabClass[];
	/** The class of the passenger currently being seated; undefined when the vessel has no classes. */
	activeClassId?: string;
	/** Seats this booking has already given to its other passengers. */
	takenOnThisBooking: string[];
	/** The seat the passenger being seated currently holds. */
	selectedSeatId?: string;
	/** The vessel's gangways and tables, so the picker draws the deck the admin drew. */
	features?: DeckFeature[];
	onSelect: (seat: SeatingTabSeat) => void;
	disabled?: boolean;
	/**
	 * Offer seats belonging to other fare types as well, drawn in their own colour.
	 *
	 * <p>For managers, when the passenger's own fare type has no seat left. Only the fare-type rule is
	 * lifted: a seat someone else holds, one given to another passenger on this booking, and a
	 * wheelchair bay stay as they are.</p>
	 */
	allowOtherFareTypes?: boolean;
}

/**
 * The seats, drawn as the deck is laid out rather than as a dropdown.
 *
 * <p>Blocks follow the vessel's own side/placement data where it has been configured, so the picker
 * matches the printed plan; where it has not, every seat still appears, in one block. Colour carries
 * the class, which is what stops a booker picking a seat their fare does not cover and wondering
 * why it was refused.</p>
 */
export default function SeatingDeck({
	seats,
	passengerClasses,
	activeClassId,
	takenOnThisBooking,
	selectedSeatId,
	features = [],
	onSelect,
	disabled = false,
	allowOtherFareTypes = false,
}: Props) {
	/*
		Colour answers one question: can I sit here. Green means yes; everything the booker cannot
		take is the same grey, whether it is occupied or sold in another fare type. Those seats used
		to be painted in their fare type's own colour, which made the row they cannot book the most
		colourful thing on the plan — it read as a choice, and bookers clicked it.

		An unavailable seat is still drawn rather than hidden: "taken" and "does not exist" are
		different things to somebody checking against the printed plan.
	*/
	const appearance = (seat: SeatingTabSeat): SeatAppearance => {
		const baseState = seatState(seat, activeClassId, takenOnThisBooking, selectedSeatId);
		const state: SeatStateKey =
			allowOtherFareTypes && baseState === 'NOT_IN_YOUR_CLASS' ? 'OTHER_FARE_TYPE' : baseState;
		const muted =
			state === 'NOT_A_SEAT' ||
			state === 'TAKEN' ||
			state === 'TAKEN_BY_THIS_BOOKING' ||
			state === 'NOT_IN_YOUR_CLASS';

		const title = (() => {
			if (seat.kind === 'WHEELCHAIR_BAY') {
				return 'Wheelchair bay — not bookable as a seat';
			}
			if (state === 'NOT_A_SEAT') {
				return 'not bookable as a seat';
			}
			if (state === 'SELECTED') {
				return 'your seat';
			}
			if (state === 'TAKEN_BY_THIS_BOOKING') {
				return 'chosen for another passenger on this booking';
			}
			if (state === 'AVAILABLE') {
				return 'available';
			}
			if (state === 'OTHER_FARE_TYPE') {
				return 'another fare type — free for this passenger';
			}
			return state === 'TAKEN' ? 'already taken' : 'not available in your fare type';
		})();

		return {
			background: SEAT_STATE_COLOURS[state],
			textClass: muted ? 'text-gray-600' : 'text-white',
			title,
			// Selected stays clickable so the booker can always release what they hold.
			selectable: (!disabled && (state === 'AVAILABLE' || state === 'OTHER_FARE_TYPE')) || state === 'SELECTED',
			outlined: state === 'NOT_A_SEAT',
		};
	};

	return (
		<div className="flex flex-col gap-4">
			{/*
				One grid for the whole deck, with the sides separated by a gap inside it rather than by
				three boxes side by side. Three boxes each sorted and sized themselves, so row 6 in the
				port block did not line up with row 6 in the centre — and a deck plan is read across,
				which is the one thing the split made impossible. This is what the admin's own grid has
				always done; the picker was the odd one out.
			*/}
			<div className="rounded-lg bg-white/70 p-3 overflow-x-auto">
				<SeatingDeckGrid seats={seats} features={features} appearance={appearance} onSelect={onSelect} />
			</div>

			{markerLegend(features).length > 0 && (
				<div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
					{markerLegend(features).map(marker => (
						<span
							key={`${marker.icon}|${marker.colour ?? ''}|${marker.label ?? ''}`}
							className="flex items-center gap-1"
						>
							<span style={{ color: marker.colour ?? '#4b5563' }} className="flex items-center">
								<Icon name={marker.icon} useMaterialIcons classname="text-sm leading-none" />
							</span>
							{marker.label ?? marker.icon}
						</span>
					))}
				</div>
			)}

			{seats.some(seat => seat.kind === 'WHEELCHAIR_BAY') && (
				<div className="flex items-center gap-1 text-xs text-gray-600">
					<Icon name="accessible" useMaterialIcons classname="text-sm leading-none" />
					Wheelchair bay — shown on the plan, not bookable as a seat
				</div>
			)}

			<div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
				{(
					[
						['AVAILABLE', 'Available'],
						...(allowOtherFareTypes
							? ([['OTHER_FARE_TYPE', 'Another fare type — free']] as [SeatStateKey, string][])
							: []),
						['SELECTED', 'Your seat'],
						['TAKEN_BY_THIS_BOOKING', 'Chosen for another passenger'],
						['TAKEN', 'Already taken'],
						// The standing state, named on the plan as well as in the card: a booker looking
						// at a deck of colours should be able to find out what happens if they pick none
						// of them.
						['NOT_CHOSEN', 'No seat chosen — assigned at check-in'],
					] as [SeatStateKey, string][]
				).map(([state, label]) => (
					<span key={state} className="flex items-center gap-1">
						<span
							className="inline-block w-3 h-3 rounded"
							style={{ background: SEAT_STATE_COLOURS[state] }}
						/>
						{label}
					</span>
				))}
			</div>

			{/*
				No key of other fare types. It listed the classes whose seats this booker cannot
				take, under a caption explaining that is what they were — three facts to read before
				learning nothing they can act on. A seat they cannot have now reads as unavailable
				like any other, which needs no legend.
			*/}
		</div>
	);
}
